// Your own second factor (T2), as the Two-factor row of Profile & security's
// Security card (R10). Per user and free on every plan — the account-wide
// "require it of everyone" policy lives on Sign-in & access, next to the other
// rules about how the team signs in.
//
// The whole flow opens under the row, inside the card: enrol (QR, the key by
// hand, the proving code, the recovery codes shown once), and turning it off
// with the password AND a current code.
import { useEffect, useState } from 'react';
import { adminMe, twoFactorDisableApi } from '../../api/admin';
import TwoFactorEnroll from '../../components/TwoFactorEnroll';
import { useToast } from '../../components/Toast';
import { RowCard } from '../../components/fs';

export default function SecuritySettings() {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [required, setRequired] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [password, setPassword] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = () => {
    adminMe()
      .then((r) => { setEnabled(!!r.twoFactorEnabled); setRequired(!!r.twoFactorRequired); setLoaded(true); })
      .catch(() => setLoaded(true));
  };
  useEffect(load, []);

  async function disable(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await twoFactorDisableApi(password, codeInput.trim());
      setDisabling(false); setPassword(''); setCodeInput('');
      toast('Two-factor authentication is off. Every other session has been signed out.');
      load();
    } catch (err: any) {
      setError(err.message || 'Could not turn two-factor off.');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <RowCard label="Two-factor authentication"><span className="st-muted">Loading…</span></RowCard>;

  return (
    <>
      <RowCard
        label="Two-factor authentication"
        actions={enabled ? (
          !disabling && (
            <button className="fs-btn text" onClick={() => { setDisabling(true); setError(''); }}>
              Turn off two-factor…
            </button>
          )
        ) : (
          !enrolling && (
            <button className="fs-btn primary sm" onClick={() => setEnrolling(true)}>
              Turn on two-factor…
            </button>
          )
        )}
      >
        {enabled ? (
          <>
            <span className="st-state ok">On.</span>{' '}
            <span className="st-muted">
              Signing in asks for your password and a six-digit code from your authenticator
              app. If you lose the phone, a recovery code signs you in once — or an Owner or
              Admin can reset two-factor for you.
            </span>
          </>
        ) : (
          <>
            <span className="st-state off">Off.</span>{' '}
            <span className="st-muted">
              With two-factor on, signing in asks for your password and a six-digit code from
              an authenticator app on your phone — so a leaked password alone cannot open your
              account.
              {required && ' Your team requires it, so your next sign-in will ask you to set it up.'}
            </span>
          </>
        )}
      </RowCard>
      {enabled && disabling && (
        <div className="st-expand">
          <form onSubmit={disable} className="st-narrow">
            <p className="st-muted">
              Turning it off asks for both factors — that is the point of having them.
              {required && ' Your team requires two-factor, so your next sign-in will make you set it up again.'}
            </p>
            {error && <div className="ad-err">{error}</div>}
            <div className="ad-field">
              <label htmlFor="tf-password">Your password</label>
              <input id="tf-password" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="ad-field">
              <label htmlFor="tf-code">Code from your app (or a recovery code)</label>
              <input id="tf-code" inputMode="numeric" autoComplete="one-time-code"
                value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
            </div>
            <div className="st-actions">
              <button className="fs-btn danger" type="submit" disabled={busy || !password || !codeInput.trim()}>
                {busy ? 'Checking…' : 'Turn it off'}
              </button>
              <button className="fs-btn" type="button" onClick={() => setDisabling(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {!enabled && enrolling && (
        <div className="st-expand">
          <TwoFactorEnroll onDone={() => { setEnrolling(false); toast('Two-factor authentication is on.'); load(); }} />
        </div>
      )}
    </>
  );
}
